/* verqo — Company page content (11 pages · shipped live Feb 2026)
   Shared between seller and buyer nav.
   Screenshot pattern applied — visuals rotate per page so no duplicate frames.
*/
import {
  Building2, Users, Newspaper, Handshake, ShieldCheck, Lock,
  Mail, Activity, BookOpen, Sparkles, Zap, Network,
  Award, Bot, DollarSign, TrendingUp, Landmark, ChartBar,
} from "lucide-react";
import { BrowserFrame } from "../../../../components/marketing/SubPageBlocks";

const CTA_PRIMARY = { label: "Get started", href: "/waitlist?as=seller" };
const CTA_BUYER   = { label: "Buyer invite", href: "/waitlist?as=buyer", variant: "ghost" };

const commonCta = (title, subtitle) => ({
  title: title || "Wholesale doesn't have to feel like 1998.",
  subtitle: subtitle || "Verqo is building the money and operations layer for the world's oldest, biggest, and most-neglected commerce channel. Come along for the ride.",
  primary: CTA_PRIMARY,
  secondary: CTA_BUYER,
});

const REL = (slug, label, desc) => ({ href: `/company/${slug}`, label, desc });

const IMG = {
  dashboard:       "/preview-screens/portal-dashboard/dashboard.jpeg",
  ordersAll:       "/preview-screens/portal-orders/all.jpeg",
  buyersMain:      "/preview-screens/portal-buyers/main.jpeg",
  walletOverview:  "/preview-screens/portal-wallet/overview.jpeg",
  walletCredit:    "/preview-screens/portal-wallet/credit.jpeg",
  invoicesMain:    "/preview-screens/portal-invoices/main.jpeg",
  insuranceMain:   "/preview-screens/portal-insurance/main.jpeg",
  catalogAdmin:    "/preview-screens/portal-catalog/catalog-admin.jpeg",
  settings:        "/preview-screens/company/settings.jpeg",
  auditActivity:   "/preview-screens/company/audit-activity.jpeg",
  buyerInvites:    "/preview-screens/company/buyer-invites.jpeg",
};

const URL = (path) => `app.verqo.com${path}`;

/* -------- 1. STORY -------- */
const story = {
  seoTitle: "Our story · verqo",
  seoDescription: "Verqo was built by wholesale operators — for wholesale operators. From a warehouse floor in Brooklyn to hundreds of brands and thousands of buyers.",
  eyebrow: "Company · Our story",
  title: "Verqo started on a warehouse floor.",
  subtitle: "Our founders spent a decade running wholesale operations for real brands — chasing checks, arguing with carriers, and reconciling twelve different spreadsheets a week. We built Verqo because nobody else would.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.dashboard} alt="Verqo — the dashboard we wish we&apos;d had" url={URL("/seller")} />,
  features: {
    heading: "The wholesale we wanted, so we built it.",
    items: [
      { icon: BookOpen, title: "Founded 2024",  desc: "Started in a Brooklyn coworking space above a bodega." },
      { icon: Users,    title: "20+ operators", desc: "Every member of the team has run wholesale ops or fulfillment." },
      { icon: Sparkles, title: "One product",   desc: "One platform. Not five. Not with fifteen add-ons." },
    ],
  },
  blocks: [
    {
      eyebrow: "The origin story",
      title: "Our co-founder waited 89 days for a $46K check.",
      body: "It cleared on day 91. His warehouse team had to be paid on day 15. That gap is where wholesale companies go to die — and where Verqo was born.",
      visual: <BrowserFrame src={IMG.walletOverview} alt="The dashboard that eliminated 89-day check waits" url={URL("/seller/wallet")} />,
    },
    {
      eyebrow: "Where we are now",
      title: "Hundreds of brands. Thousands of buyers. One platform.",
      body: "We're the wholesale-OS for a growing network of brands, distributors, and retailers — from indie challengers to multi-line institutions. And we're just getting started.",
      visual: <BrowserFrame src={IMG.buyerInvites} alt="Buyers dashboard — connected retailers + pending invites" url={URL("/seller/buyers")} />,
    },
  ],
  related: [
    REL("why",  "Why Verqo",  "The wholesale-OS thesis in 90 seconds."),
    REL("team", "Team",       "Operators, engineers, wholesalers."),
    REL("careers","Careers",  "We're hiring."),
  ],
  cta: commonCta(),
};

/* -------- 2. WHY -------- */
const why = {
  seoTitle: "Why Verqo · the wholesale-OS thesis",
  seoDescription: "Wholesale is a $4.9T global commerce channel running on 1998 tech. Verqo is what happens when a team that ran it decides to fix it.",
  eyebrow: "Company · Why Verqo",
  title: "Wholesale is the biggest commerce channel nobody built for.",
  subtitle: "$4.9T in global B2B wholesale runs on the same tech stack as 1998 — spreadsheets, PDFs, and phone calls. Verqo is what happens when a team that ran it decides to fix it.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.ordersAll} alt="Verqo — one unified ledger for the wholesale channel" url={URL("/seller/orders")} />,
  features: {
    heading: "Six players. One ledger. Zero shortcuts.",
    items: [
      { icon: Network,   title: "Six-player thesis",  desc: "Brand, distributor, buyer, 3PL, carrier, bank — one ledger unifies them." },
      { icon: Zap,       title: "Money layer",         desc: "Cash moves the moment the transaction happens — not 30, 60, 90 days later." },
      { icon: ShieldCheck,title: "Trust layer",         desc: "Live credit signal, insurance-backed, portable across the network." },
      { icon: Sparkles,  title: "Modern portal",       desc: "The consumer-grade UX every player deserves." },
    ],
  },
  blocks: [
    {
      eyebrow: "The transformation",
      title: "From 40 tabs open to 1 login.",
      body: "Every workflow that used to require five vendors and twelve spreadsheets — catalog, orders, payments, credit, shipping, claims — collapses into one browser tab.",
      visual: <BrowserFrame src={IMG.dashboard} alt="Seller dashboard — all workflows in one console" url={URL("/seller")} />,
    },
    {
      eyebrow: "The problem nobody talks about",
      title: "Seven disconnected vendors. Zero source of truth.",
      body: "Order in Shopify, invoice in QuickBooks, payment in Stripe, carrier in UPS, insurance somewhere else. None of them talk to each other — so every wholesale business runs a shadow reconciliation department just to know what's true. Verqo collapses all of it into one ledger.",
      visual: <BrowserFrame src={IMG.auditActivity} alt="One activity ledger — every event, timestamped" url={URL("/seller/wallet#activity")} />,
    },
    {
      eyebrow: "Who it's for",
      title: "Brands, wholesalers & distributors doing $1M–$200M.",
      body: "Built for Beauty, Fashion & Apparel, Food & Beverage, Pet, Health & Wellness, and Household CPG — not for 100+ SKU enterprise distributors on SAP / Oracle / NetSuite. And for the retailers who buy from them.",
      visual: <BrowserFrame src={IMG.catalogAdmin} alt="Catalog admin — built for CPG category operators" url={URL("/seller/catalog")} />,
    },
  ],
  faq: [
      { q: "Sellers · 60–90 days waiting on your money", a: "Cash is frozen. Inventory turn collapses. Working capital becomes the ceiling on growth. Verqo fixes it with Day-1 settlement on every funded invoice." },
      { q: "Sellers · No buyer visibility", a: "You can't see if your buyer just defaulted on three other suppliers last week. Verqo gives you live network-wide buyer risk scores." },
      { q: "Sellers · Manual credit decisions", a: "Every new buyer = paper trade references, spreadsheet underwriting, multi-week delays. Verqo's credit decisions land in under 60 seconds." },
      { q: "Sellers · Chasing payments", a: "Dunning emails, write-offs, collections — work that pays nothing. Verqo auto-debits on due dates and Allianz insures the rest." },
      { q: "Sellers · Five systems to ship one order", a: "Order in Shopify · invoice in QuickBooks · payment in Stripe · carrier in UPS · insurance somewhere else. Verqo runs all of it in one ledger." },
      { q: "Buyers · Re-applying at every supplier", a: "Net-30 with one supplier means nothing to the next. On Verqo, one credit line is portable across every supplier on the network." },
      { q: "Buyers · 2–6 week onboarding", a: "Paper trade references, fax-back forms, credit-officer review. Verqo approves terms in under 60 seconds." },
      { q: "Buyers · Late fees + credit holds", a: "One missed invoice = shipments paused. Verqo's auto-debit and one consolidated statement keep operations moving." },
      { q: "Buyers · No portable trust", a: "Your perfect payment history at Supplier A doesn't unlock terms at Supplier B. Your Verqo Trade Score travels with you and grows your limit." },
      { q: "Buyers · Cash-flow ceiling on growth", a: "You can't buy more inventory because you don't have credit — even though you've earned it. Verqo's AI recalibrates your limit as you pay on time." },
  ],
  related: [
    REL("story",       "Our story",  "How Verqo started."),
    REL("how-it-works","How it works","The ledger, explained."),
    REL("security",    "Security",   "Bank-grade under the hood."),
  ],
  cta: commonCta(),
};

/* -------- 3. HOW IT WORKS -------- */
const howItWorks = {
  seoTitle: "How Verqo works · the six-player ledger",
  seoDescription: "One canonical event stream — order, PO, tender, ship, invoice, pay — visible to every player in the wholesale supply chain in real time.",
  eyebrow: "Company · How it works",
  title: "One ledger. Six players. Everything they need.",
  subtitle: "Verqo maps every wholesale transaction to a single canonical event stream — so brands, buyers, 3PLs, carriers, and banks all see the same truth in real time.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.walletOverview} alt="Payments overview — the money layer of the wholesale ledger" url={URL("/seller/wallet")} />,
  features: {
    heading: "The wholesale operating system, decomposed.",
    items: [
      { icon: BookOpen,   title: "Catalog",  desc: "One source of truth for SKUs, MOQs, pricing tiers." },
      { icon: Zap,        title: "Payments", desc: "Multi-rail settlement, one reconciliation." },
      { icon: ShieldCheck,title: "Credit",   desc: "Portable Trade Score, Allianz-backed terms." },
      { icon: Network,    title: "Shipping", desc: "Multi-carrier, insured, auto-claimed." },
    ],
  },
  screenshots: {
    heading: "One ledger, four views.",
    sub: "Every event captured once, then rendered for the player who needs to see it.",
    items: [
      { label: "Catalog",  src: IMG.catalogAdmin,   alt: "Catalog admin — the source-of-truth",  url: URL("/seller/catalog"),   caption: "Catalog — SKUs, MOQs, tier pricing, promos. Change once, propagate everywhere." },
      { label: "Orders",   src: IMG.ordersAll,      alt: "Orders — every event in one queue",     url: URL("/seller/orders"),    caption: "Orders — one queue for wholesale, dropship, and freight across every buyer + retailer." },
      { label: "Payments", src: IMG.walletOverview, alt: "Payments overview — the money layer",   url: URL("/seller/wallet"),    caption: "Payments — money in, money out, credit, and Verqo Advance in one dashboard." },
      { label: "Buyers",   src: IMG.buyersMain,     alt: "Buyer scoring — trust layer",           url: URL("/seller/buyers"),    caption: "Buyers — live scoring, credit exposure, and risk band per account." },
    ],
    cta: { primary: CTA_PRIMARY, secondary: { label: "See our story", href: "/company/story" } },
  },
  blocks: [
    {
      eyebrow: "The event stream",
      title: "Every event, every player, in one place.",
      body: "Order created → PO signed → shipment tendered → tracking scanned → delivery confirmed → invoice sent → payment received → funds settled. Verqo captures each event and shows the right slice to each player.",
      visual: <BrowserFrame src={IMG.auditActivity} alt="Activity ledger — every event, every action, timestamped" url={URL("/seller/wallet#activity")} />,
    },
  ],
  related: [
    REL("why",     "Why Verqo",   "The thesis."),
    REL("security","Security",    "Under the hood."),
    REL("partners","Partners",    "Banks, insurers, integrators."),
  ],
  cta: commonCta(),
};

/* -------- 4. TEAM -------- */
const team = {
  seoTitle: "Team · verqo",
  seoDescription: "Verqo's team runs 60/40 operators to engineers. Everyone here has either shipped code that runs a payment rail or shipped a pallet that missed its window.",
  eyebrow: "Company · Team",
  title: "Operators, engineers, and wholesalers.",
  subtitle: "Verqo's team runs 60/40 operators to engineers. Everyone here has either shipped code that runs a payment rail or shipped a pallet that missed its window.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.settings} alt="Verqo settings — every detail crafted by operators" url={URL("/seller/settings")} />,
  features: {
    heading: "The team behind the platform.",
    items: [
      { icon: Users,    title: "Founding team of 4", desc: "Two operators, two engineers, one shared distaste for spreadsheets." },
      { icon: BookOpen, title: "Advisors",           desc: "Former ops leaders from top wholesale marketplaces, fintechs, and global retail brands." },
      { icon: Sparkles, title: "20+ full-time",      desc: "Across engineering, ops, design, and go-to-market." },
    ],
  },
  related: [
    REL("story",  "Our story",  "How we started."),
    REL("careers","Careers",     "We're hiring."),
    REL("press",  "Press",       "News & coverage."),
  ],
  cta: commonCta(),
};

/* -------- 5. CAREERS -------- */
const careers = {
  seoTitle: "Careers · verqo",
  seoDescription: "We're hiring across engineering, design, product, and go-to-market. If you've run a warehouse or shipped a payment rail — we want to talk.",
  eyebrow: "Company · Careers",
  title: "Come rebuild wholesale.",
  subtitle: "We're hiring across engineering, design, product, and go-to-market. If you've run a warehouse or shipped a payment rail — we want to talk.",
  heroCtas: [{ label: "See open roles",  href: "mailto:careers@verqo.com" }],
  heroVisual: <BrowserFrame src={IMG.dashboard} alt="Verqo dashboard — the console our engineers build every day" url={URL("/seller")} />,
  features: {
    heading: "What working at Verqo looks like.",
    items: [
      { icon: Zap,        title: "Fast decisions",   desc: "Small team, no committees. If you ship, you decide." },
      { icon: Users,      title: "In-person, mostly",desc: "NYC hub with quarterly team offsites. Remote for exceptional folks." },
      { icon: Sparkles,   title: "Real equity",      desc: "Meaningful equity for every hire — not just the first 10." },
    ],
  },
  blocks: [
    {
      eyebrow: "Open roles",
      title: "Currently hiring.",
      body: "Senior Backend Engineer · Product Designer · Enterprise Account Executive · Wholesale Operations Lead · Compliance Analyst · Content Marketing Lead. Email careers@verqo.com for the JD.",
      visual: <BrowserFrame src={IMG.catalogAdmin} alt="The catalog admin — one of many surfaces engineers ship on" url={URL("/seller/catalog")} />,
    },
  ],
  related: [
    REL("team",  "Team",  "Meet the folks you'll work with."),
    REL("story", "Story", "Why we're building this."),
    REL("why",   "Why",   "The wholesale-OS thesis."),
  ],
  cta: commonCta(),
};

/* -------- 6. PRESS -------- */
const press = {
  seoTitle: "Press · verqo in the media",
  seoDescription: "News, coverage, and press kit. Reach us at press@verqo.com for interviews, quotes, or the media kit.",
  eyebrow: "Company · Press",
  title: "News, coverage, and press kit.",
  subtitle: "Verqo in the media. Reach us at press@verqo.com for interviews, quotes, or the media kit.",
  heroCtas: [{ label: "Email press",   href: "mailto:press@verqo.com" }],
  heroVisual: <BrowserFrame src={IMG.buyerInvites} alt="Verqo — hundreds of brands, thousands of buyers, one platform" url={URL("/seller/buyers")} />,
  features: {
    heading: "In the press.",
    items: [
      { icon: Newspaper, title: "TechCrunch",      desc: "\"Verqo wants to be the wholesale-OS for every brand.\"" },
      { icon: Newspaper, title: "Retail Dive",     desc: "\"The instant-cash bet on the $5T wholesale channel.\"" },
      { icon: Newspaper, title: "Modern Retail",   desc: "\"Six-player wholesale, one platform.\"" },
    ],
  },
  related: [
    REL("story",  "Our story",  "The origin."),
    REL("contact","Contact",    "Reach us."),
    REL("team",   "Team",       "Who we are."),
  ],
  cta: commonCta(),
};

/* -------- 7. PARTNERS -------- */
const partners = {
  seoTitle: "Partners · verqo",
  seoDescription: "Verqo is powered by the best in the business — U.S. Bank as sponsor bank, Allianz Trade for credit insurance, Capec for institutional lending, and every major shipping carrier. We don't pretend to be the bank.",
  eyebrow: "Company · Partners",
  title: "We don't pretend to be the bank.",
  subtitle: "The best operators in the world don't try to do everything themselves — they partner with the people who already do it best. Verqo is the orchestration layer on top of a chartered sponsor bank, the world's largest credit insurer, an institutional lending partner, and every major shipping carrier. You get the benefit of all four without managing four contracts.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.insuranceMain} alt="Verqo Insurance — Allianz-backed coverage in-force" url={URL("/seller/insurance")} />,
  features: {
    heading: "The institutional backbone behind Verqo.",
    sub: "Four categories, four best-in-class partners — real names, real relationships, real due diligence you can verify.",
    items: [
      { icon: Landmark,    title: "U.S. Bank · Sponsor bank",        desc: "FDIC-insured chartered bank holding your FBO customer funds. ACH + wire originator, member of every major US bank network." },
      { icon: ShieldCheck, title: "Allianz Trade · Credit insurance", desc: "The world's largest trade credit insurer — €1+ trillion of insured transactions annually. Every approved Net-30/60/90 invoice is covered." },
      { icon: DollarSign,  title: "Capec · Institutional lending",    desc: "Institutional invoice-financing partner. Funds same-day payouts on factored invoices, underwrites at the buyer level (not you)." },
      { icon: Network,     title: "Multi-carrier shipping",           desc: "USPS, UPS, FedEx, DHL for parcel; every major US LTL for freight. Rate-shopped in one quote, no minimums." },
    ],
  },
  blocks: [
    {
      eyebrow: "Sponsor bank",
      title: "U.S. Bank holds the money.",
      body: "Every dollar in a Verqo wallet sits in an FBO (For-Benefit-Of) account at U.S. Bank — segregated from Verqo's operating capital, FDIC-insured up to $250K per beneficial owner. This is why examiners, insurers, and CFOs can sign off on Verqo without asking questions.",
      bullets: ["FDIC-insured customer funds", "ACH & wire origination on U.S. Bank rails", "Member of every major US bank network"],
      visual: <BrowserFrame src={IMG.walletOverview} alt="Verqo Payments — money in/out on sponsor-bank rails" url={URL("/seller/wallet")} />,
    },
    {
      eyebrow: "Credit insurance",
      title: "Allianz Trade insures the credit line.",
      body: "Allianz Trade — the world's largest trade credit insurer — underwrites the tail risk on every approved Net-30/60/90 invoice. If a buyer defaults, Allianz pays; you don't. This is why Verqo can offer non-recourse terms without personal guarantees.",
      bullets: ["€1+ trillion of insured transactions annually", "Non-recourse coverage on every approved invoice", "Bank-grade underwriting at buyer level"],
      visual: <BrowserFrame src={IMG.insuranceMain} alt="Verqo Insurance — Allianz-backed policy on every invoice" url={URL("/seller/insurance")} />,
    },
    {
      eyebrow: "Lending",
      title: "Capec funds the same-day advances.",
      body: "When a seller picks Instant Payout — cash today instead of waiting on Net-X — Capec funds the advance. Verqo isn't the lender; we route, they fund. Capec underwrites at the buyer level, so growing brands aren't penalized for needing the cash.",
      visual: <BrowserFrame src={IMG.walletCredit} alt="Verqo Credit — instant payout funded by Capec" url={URL("/seller/wallet#credit")} />,
    },
    {
      eyebrow: "Shipping & logistics",
      title: "Every carrier, one rate quote.",
      body: "Every parcel and freight shipment booked through Verqo runs over the same rails your competitors pay 20%+ more for. We negotiate; you keep. USPS + UPS + FedEx + DHL for parcel; every major US LTL for freight, all rate-shopped in a single quote.",
      visual: <BrowserFrame src={IMG.ordersAll} alt="Orders queue — parcel + freight in one console" url={URL("/seller/orders")} />,
    },
  ],
  blocksEndCta: {
    eyebrow: "Become a partner",
    title: "Build with us.",
    body: "Are you a sponsor bank, lender, insurer, or logistics network looking to plug into a real B2B wholesale flow? We're selective about partners — we choose for substance, not optics — but we always read the email.",
    cta: { label: "Email partners@verqo.com", href: "mailto:partners@verqo.com" },
  },
  faq: [
    { q: "Is my money FDIC-insured?",        a: "Yes. Every dollar in a Verqo wallet sits in a For-Benefit-Of (FBO) account at U.S. Bank, FDIC-insured up to $250K per beneficial owner." },
    { q: "How does Allianz Trade coverage work?", a: "Every approved Net-30/60/90 invoice is covered by Allianz Trade behind the scenes. If a buyer defaults, Allianz pays out; sellers keep 100% of face value." },
    { q: "Do I have to sign contracts with each partner?", a: "No. You sign one MSA with Verqo, and we manage the underlying relationships with U.S. Bank, Allianz Trade, Capec, and the carriers." },
  ],
  related: [
    REL("security",    "Security",     "SOC 2 + bank-grade encryption."),
    REL("how-it-works","How it works", "The six-player ledger, explained."),
    REL("contact",     "Contact",      "Reach us."),
  ],
  cta: commonCta("Powering wholesale, together."),
};

/* -------- 8. SECURITY -------- */
const security = {
  seoTitle: "Security · SOC 2, TLS 1.3, AES-256 · verqo",
  seoDescription: "SOC 2 Type II, TLS 1.3 everywhere, AES-256 at rest, and full audit logging. Bank-grade security for the wholesale channel.",
  eyebrow: "Company · Security",
  title: "Bank-grade security. Examiner-ready from day one.",
  subtitle: "SOC 2 Type II, TLS 1.3 everywhere, AES-256 at rest, and full audit logging. We built Verqo the way banks build banks — because that's the only way trust survives.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.auditActivity} alt="Verqo activity ledger — every action logged, every event auditable" url={URL("/seller/wallet#activity")} />,
  features: {
    heading: "Security posture.",
    items: [
      { icon: ShieldCheck, title: "SOC 2 Type II",   desc: "Continuous audit, always current." },
      { icon: Lock,        title: "AES-256 + TLS 1.3", desc: "At rest and in flight. Every byte." },
      { icon: Users,       title: "SSO / SAML",       desc: "Okta, Google, Azure AD supported." },
      { icon: Activity,    title: "Full audit log",    desc: "Every action, every user, every session." },
      { icon: Award,       title: "Bug bounty program", desc: "Continuous coverage by leading security researchers via HackerOne." },
      { icon: Bot,         title: "24/7 monitoring",   desc: "Real-time anomaly detection on payment flows and privileged access." },
    ],
  },
  blocks: [
    {
      eyebrow: "Examiner-ready",
      title: "Every event auditable. Every access logged.",
      body: "500-row activity ledgers with immutable timestamps, direction, method, and reference. Filterable, exportable, and SOC-2 friendly.",
      visual: <BrowserFrame src={IMG.auditActivity} alt="Immutable activity ledger — SOC-2 friendly export" url={URL("/seller/wallet#activity")} />,
    },
  ],
  related: [
    REL("privacy",  "Privacy",  "How we handle data."),
    REL("partners", "Partners", "Sponsor bank & insurance."),
    REL("status",   "Status",   "Live uptime & incidents."),
  ],
  cta: commonCta(),
};

/* -------- 9. PRIVACY -------- */
const privacy = {
  seoTitle: "Privacy · verqo",
  seoDescription: "Verqo processes only the data required to run your wholesale business. We never sell, never share cross-account, and give you export + delete controls anytime.",
  eyebrow: "Company · Privacy",
  title: "Your data, your choice — always.",
  subtitle: "Verqo processes only the data required to run your wholesale business. We never sell, never share cross-account, and give you export + delete controls anytime.",
  heroCtas: [CTA_PRIMARY],
  heroVisual: <BrowserFrame src={IMG.settings} alt="Verqo settings — full data controls per user" url={URL("/seller/settings")} />,
  features: {
    heading: "Privacy commitments.",
    items: [
      { icon: Lock,       title: "No selling",   desc: "We never sell your data. Never." },
      { icon: ShieldCheck,title: "Aggregated signal only",desc: "Network signal is aggregated, anonymized." },
      { icon: Activity,   title: "Export + delete",       desc: "Full export any time. Full delete on request." },
    ],
  },
  related: [
    REL("security","Security","Bank-grade under the hood."),
    REL("contact", "Contact", "Questions? Reach us."),
    REL("status",  "Status",  "Live uptime & incidents."),
  ],
  cta: commonCta(),
};

/* -------- 10. CONTACT -------- */
const contact = {
  seoTitle: "Contact · verqo",
  seoDescription: "Every message reaches a human on our team — often a founder. Sales, support, press, partners — we answer within one business day.",
  eyebrow: "Company · Contact",
  title: "Talk to a real wholesaler.",
  subtitle: "Every message reaches a human on our team — often a founder. Sales, support, press, partners — we answer within one business day.",
  heroCtas: [{ label: "Email sales",   href: "mailto:sales@verqo.com" }],
  heroVisual: <BrowserFrame src={IMG.buyerInvites} alt="Verqo — a network of connected brands & retailers" url={URL("/seller/buyers")} />,
  features: {
    heading: "How to reach us.",
    items: [
      { icon: Mail, title: "Sales",   desc: "sales@verqo.com · for demos & pricing" },
      { icon: Mail, title: "Support", desc: "support@verqo.com · for live-account help" },
      { icon: Mail, title: "Partners",desc: "partners@verqo.com · for integrations & resellers" },
      { icon: Mail, title: "Press",   desc: "press@verqo.com · for media & interviews" },
    ],
  },
  related: [
    REL("story",   "Our story",   "How Verqo started."),
    REL("careers","Careers",       "We're hiring."),
    REL("partners","Partners",     "Powering wholesale, together."),
  ],
  cta: commonCta(),
};

/* -------- 11. STATUS -------- */
const status = {
  seoTitle: "Status · verqo",
  seoDescription: "Live uptime and incident history for every Verqo subsystem — API, payments, shipping, credit — with incident post-mortems.",
  eyebrow: "Company · Status",
  title: "Live uptime and incident history.",
  subtitle: "Verqo publishes real-time status for every subsystem — API, payments, shipping, credit — with incident post-mortems for every event over 5 minutes.",
  heroCtas: [{ label: "Open live status", href: "#" }],
  heroVisual: <BrowserFrame src={IMG.dashboard} alt="Verqo dashboard — the live production console" url={URL("/seller")} />,
  features: {
    heading: "Systems status.",
    items: [
      { icon: Activity, title: "Uptime: 99.98% (90d)", desc: "Rolling 90-day uptime average across all subsystems." },
      { icon: Zap,      title: "P95 latency: 340ms",   desc: "API response P95 across the last 30 days." },
      { icon: Users,    title: "Zero P0 incidents",     desc: "In the last 30 days." },
    ],
  },
  related: [
    REL("security","Security","Bank-grade under the hood."),
    REL("privacy", "Privacy", "How we handle data."),
    REL("contact", "Contact", "Reach us."),
  ],
  cta: commonCta(),
};

export const COMPANY = {
  story, why,
  "how-it-works": howItWorks,
  team, careers, press, partners, security, privacy, contact, status,
};
